<template>
    <div class="rd">
        <div class="pagina">
            <header class="paginakop">
                <div class="tekst">
                    <nav class="kruimel" aria-label="Breadcrumb">
                        <router-link :to="{ name: 'bookings' }">Bookings</router-link>
                        <span>/</span>
                        <span>Edit</span>
                    </nav>
                    <h1>Edit booking</h1>
                    <p>Change the game, customer or time. The date stays fixed; pick a new time below.</p>
                </div>
            </header>
            <div v-if="loading" class="leeg"><h2>Loading booking…</h2></div>
            <booking-form v-else-if="booking && locations" :locations="locations" v-model="booking" :readonlyFields="['eventDate']">
                <button class="btn btn-pink" type="button" :disabled="saving" @click="saveBooking">
                    {{ saving ? "Saving…" : "Save changes" }}
                </button>
                <router-link class="btn btn-stil" :to="{ name: 'view-booking', params: { id: route.params.id } }">Cancel</router-link>
            </booking-form>
        </div>
    </div>
</template>

<script setup>
import * as Sentry from "@sentry/vue";
import dayjs from "dayjs";
import { onMounted, ref } from "vue";

import { useRoute, useRouter } from "vue-router/composables";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import { fetchBookingData } from "../../api/graphql/bookings";
import BookingForm from "../booking/BookingForm.vue";
import { updateBooking, updateClient, updateTeam, getBooking } from "../../api/rest";
import { getExperiencesSimple } from "../../api/graphql/experiences";
import { Message } from "element-ui";


const route = useRoute();
const router = useRouter();

const loading = ref(true);
const saving = ref(false);
const originalBooking = ref(null);
const updatedBooking = ref(null);
const experiences = ref([]);

const locations = ref([]);

const booking = ref(null);

onMounted(async () => {
    locations.value = await getUserLocationsSimple();
    experiences.value = (await getExperiencesSimple()).filter((exp) => exp.flexible || exp.application?.type_for_xcube === "game");
    const bookingId = route.params.id;
    const existingBooking = await fetchBookingData(bookingId);
    booking.value = {
        xcube_id : existingBooking.xcube.id,
        experience_id : existingBooking.experience.id,
        team_1_size : Number(existingBooking.player_count),
        first_name : existingBooking.client.first_name,
        insertion : existingBooking.client.insertion,
        last_name : existingBooking.client.last_name,
        name : [existingBooking.client.first_name, existingBooking.client.insertion, existingBooking.client.last_name].filter(Boolean).join(" "),
        email : existingBooking.client.email,
        team_1_name : existingBooking.team.name,
        event_date : dayjs(existingBooking.date).toDate(),
        event_time : dayjs(existingBooking.date).toDate(),
        language : existingBooking.language,
        phone_number : existingBooking.client.phone_number,
    }
    if (locations.value.length === 1) {
        booking.value.location_id = locations.value[0].id;
    }
    console.log("Loaded booking for edit:", booking.value);
    originalBooking.value = existingBooking;
    loading.value = false;
});

const saveBooking = async () => {
    const time = dayjs(booking.value.event_time);
    booking.value.event_datetime = dayjs(booking.value.event_date)
        .hour(time.hour())
        .minute(time.minute())
        .toDate();
    try {
        saving.value = true;
        if (
            Number(originalBooking.value.xcube.id) !== booking.value.xcube_id ||
            Number(originalBooking.value.experience.id) !== booking.value.experience_id ||
            originalBooking.value.player_count !== booking.value.team_1_size ||
            originalBooking.value.language !== booking.value.language
        ) {
            const bookingUpdate = {
                xcube: {
                    id: Number(booking.value.xcube_id),
                },
                experience: {
                    id: Number(booking.value.experience_id),
                },
                player_count: booking.value.team_1_size,
                language: booking.value.language,
            }
            await updateBooking(originalBooking.value.id, bookingUpdate);
        }
        if (originalBooking.value.client.first_name !== booking.value.first_name || originalBooking.value.client.insertion !== booking.value.insertion || originalBooking.value.client.last_name !== booking.value.last_name || originalBooking.value.client.email !== booking.value.email) {
            const clientUpdate = {
                first_name: booking.value.first_name,
                insertion: booking.value.insertion,
                last_name: booking.value.last_name,
                email: booking.value.email,
            }
            await updateClient(originalBooking.value.client.id, clientUpdate);
        }
        if (originalBooking.value.team.name !== booking.value.team_1_name) {
            const teamUpdate = {
                name: booking.value.team_1_name,
            }
            await updateTeam(originalBooking.value.team.id, teamUpdate);
        }
        const data = await getBooking(originalBooking.value.id);
        updatedBooking.value = data;
        Message.success("Booking updated");
        router.push({ name: 'view-booking', params: { id: updatedBooking.value.id } });

    } catch (e) {
        Message.error({ message: `Error updating booking: ${e.message}` });
        Sentry.captureException(e);
    } finally {
        saving.value = false;
    }
};
</script>
